/**
 * AES-GCM encrypt/decrypt helpers.
 * Ciphertext and IV are stored as base64 strings.
 */

/**
 * Encrypt a Uint8Array (or ArrayBuffer) with an AES-GCM CryptoKey.
 * Returns { ciphertext: string (base64), iv: string (base64) }.
 *
 * @param {Uint8Array|ArrayBuffer} data
 * @param {CryptoKey} key
 * @returns {Promise<{ ciphertext: string, iv: string }>}
 */
export async function encryptBytes(data, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const cipherbuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    data,
  );

  return {
    ciphertext: _toBase64(new Uint8Array(cipherbuf)),
    iv:         _toBase64(iv),
  };
}

/**
 * Decrypt an { ciphertext, iv } object with an AES-GCM CryptoKey.
 * Returns the decrypted ArrayBuffer, or throws if the key is wrong.
 *
 * @param {{ ciphertext: string, iv: string }} encrypted
 * @param {CryptoKey} key
 * @returns {Promise<ArrayBuffer>}
 */
export async function decryptBytes(encrypted, key) {
  const ciphertext = _fromBase64(encrypted.ciphertext);
  const iv         = _fromBase64(encrypted.iv);

  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext,
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function _toBase64(uint8) {
  return btoa(String.fromCharCode(...uint8));
}

function _fromBase64(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}
